import React, { useEffect, useState, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import sharcLogo from './sharc_nav_logo.png';
import ArtistList from './pages/ArtistList';
import ArtistDetail from './pages/ArtistDetail';
import VenueList from './pages/VenueList';
import VenueDetail from './pages/VenueDetail';
import AddArtist from './pages/AddArtist';
import AddVenue from './pages/AddVenue';
import AddEvent from './pages/AddEvent';
import EventList from './pages/EventList';
import EventDetail from './pages/EventDetail';
import EditArtist from './pages/EditArtist';
import EditVenue from './pages/EditVenue';
import EditEvent from './pages/EditEvent';
import SharcIdPage from './pages/SharcIdPage';
import MyProfile from './pages/MyProfile';
import MyShows from './pages/MyShows';
import MyImages from './pages/MyImages';
import ProductionRoleMapping from './pages/ProductionRoleMapping';
import MemberList from './pages/MemberList';
import MemberDetail from './pages/MemberDetail';
import AddMember from './pages/AddMember';
import EditMember from './pages/EditMember';
import FestivalList from './pages/FestivalList';
import FestivalDetail from './pages/FestivalDetail';
import AddFestival from './pages/AddFestival';
import EditFestival from './pages/EditFestival';
import PromoterList from './pages/PromoterList';
import PromoterDetail from './pages/PromoterDetail';
import AddPromoter from './pages/AddPromoter';
import EditPromoter from './pages/EditPromoter';
import ProjectList from './pages/ProjectList';
import ProjectDetail from './pages/ProjectDetail';
import AddProject from './pages/AddProject';
import EditProject from './pages/EditProject';
import Home from './pages/Home';
import HowItWorks from './pages/HowItWorks';
import MyShowsTutorial from './pages/MyShowsTutorial';
import AdminPortal from './pages/AdminPortal';
import AdminPicklists from './pages/AdminPicklists';
import AdminBulkEditArtists from './pages/AdminBulkEditArtists';
import ChangeLog from './pages/ChangeLog';
import Footer from './pages/Footer';
import config from './config';
import './App.css';

// ── Object config (alphabetical) ─────────────────────────────────────────────
const OBJECTS = [
    {
        key: 'artists',
        label: 'Artists',
        addLabel: 'Artist',
        endpoint: '/artists',
        listPath: '/artists',
        addPath: '/add-artist',
        getLabel: r => r.name,
        getSublabel: r => [r.city, r.state].filter(Boolean).join(', '),
        getPath: r => `/artists/${r.slug || r.id}`,
    },
    {
        key: 'events',
        label: 'Events',
        addLabel: 'Event',
        endpoint: '/events',
        listPath: '/events',
        addPath: '/add-event',
        getLabel: r => {
            if (!r.event_date) return r.headliner || '(no headliner)';
            const d = new Date(r.event_date).toLocaleDateString('en-US', {
                year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
            });
            return r.headliner ? `${r.headliner} — ${d}` : d;
        },
        getSublabel: r => [r.venue_name, r.venue_city].filter(Boolean).join(' · '),
        getPath: r => `/events/${r.slug || r.id}`,
    },
    {
        key: 'festivals',
        label: 'Festivals',
        addLabel: 'Festival',
        endpoint: '/festivals',
        listPath: '/festivals',
        addPath: '/add-festival',
        getLabel: r => r.name,
        getSublabel: () => '',
        getPath: r => `/festivals/${r.slug || r.id}`,
        extractData: d => d.festivals || d,
    },
    {
        key: 'promoters',
        label: 'Promoters',
        addLabel: 'Promoter',
        endpoint: '/promoters',
        listPath: '/promoters',
        addPath: '/add-promoter',
        getLabel: r => r.name,
        getSublabel: r => [r.city, r.state].filter(Boolean).join(', '),
        getPath: r => `/promoters/${r.slug || r.id}`,
        extractData: d => d.promoters || d,
    },
    {
        key: 'members',
        label: 'Members',
        addLabel: 'Member',
        endpoint: '/members',
        listPath: '/members',
        addPath: '/add-member',
        getLabel: r => r.name,
        getSublabel: r => [r.hometown_city, r.hometown_state].filter(Boolean).join(', '),
        getPath: r => `/members/${r.slug || r.id}`,
        extractData: d => d.members || d,
    },
    {
        key: 'projects',
        label: 'Projects',
        addLabel: 'Project',
        endpoint: '/projects',
        listPath: '/projects',
        addPath: '/add-project',
        getLabel: r => r.name,
        getSublabel: r => r.entity_name || '',
        getPath: r => `/projects/${r.id}`,
        extractData: d => Array.isArray(d) ? d : [],
    },
    {
        key: 'venues',
        label: 'Venues',
        addLabel: 'Venue',
        endpoint: '/venues',
        listPath: '/venues',
        addPath: '/add-venue',
        getLabel: r => r.name,
        getSublabel: r => [r.city, r.state].filter(Boolean).join(', '),
        getPath: r => `/venues/${r.slug || r.id}`,
    },
];

// ── Global Search ─────────────────────────────────────────────────────────────
function GlobalSearch() {
    const navigate = useNavigate();
    const [selectedObjKey, setSelectedObjKey] = useState('artists');
    const [query, setQuery] = useState('');
    const [records, setRecords] = useState([]);
    const [loadedKey, setLoadedKey] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    const obj = OBJECTS.find(o => o.key === selectedObjKey);

    const loadRecords = useCallback(async (key) => {
        if (loadedKey === key) return;
        const o = OBJECTS.find(x => x.key === key);
        if (!o) return;
        setLoading(true);
        try {
            const res = await axios.get(`${config.API_URL}${o.endpoint}`);
            const data = o.extractData ? o.extractData(res.data) : res.data;
            setRecords(Array.isArray(data) ? data : []);
            setLoadedKey(key);
        } catch (e) {
            setRecords([]);
        }
        setLoading(false);
    }, [loadedKey]);

    const handleObjChange = (e) => {
        const key = e.target.value;
        setSelectedObjKey(key);
        setQuery('');
        setOpen(false);
        setLoadedKey(null);
        setRecords([]);
    };

    const handleFocus = () => {
        loadRecords(selectedObjKey);
        setOpen(true);
    };

    const handleInputChange = (e) => {
        setQuery(e.target.value);
        setOpen(true);
    };

    useEffect(() => {
        function onClickOutside(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const q = query.trim().toLowerCase();
    const filtered = q
        ? records.filter(r =>
            obj.getLabel(r).toLowerCase().includes(q) ||
            obj.getSublabel(r).toLowerCase().includes(q)
          )
        : [];

    const handleSelect = (r) => {
        navigate(obj.getPath(r));
        setQuery('');
        setOpen(false);
        if (inputRef.current) inputRef.current.blur();
    };

    const showDropdown = open && q.length > 0 && (loading || filtered.length > 0 || !loading);

    return (
        <div ref={containerRef} style={N.searchOuter}>

            {/* Object type selector */}
            <div style={N.objSelectWrap}>
                <select
                    value={selectedObjKey}
                    onChange={handleObjChange}
                    style={N.objSelect}
                >
                    {OBJECTS.map(o => (
                        <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                </select>
                <span style={N.objChevron}>▾</span>
            </div>

            {/* Text search input */}
            <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    placeholder={`Search ${obj.label.toLowerCase()}...`}
                    style={{
                        ...N.searchInput,
                        borderRadius: showDropdown ? '0 4px 0 0' : '0 4px 4px 0',
                    }}
                    autoComplete="off"
                />

                {showDropdown && (
                    <div style={N.searchDropdown}>
                        {loading && (
                            <div style={N.searchMeta}>Loading {obj.label.toLowerCase()}…</div>
                        )}
                        {!loading && filtered.length === 0 && q && (
                            <div style={N.searchMeta}>No results for "{query}"</div>
                        )}
                        {!loading && filtered.map((r, i) => (
                            <div
                                key={r.id || i}
                                onMouseDown={() => handleSelect(r)}
                                style={N.searchRow}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#383838'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                <span style={N.searchRowLabel}>{obj.getLabel(r)}</span>
                                {obj.getSublabel(r) && (
                                    <span style={N.searchRowSub}>{obj.getSublabel(r)}</span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Browse Dropdown ───────────────────────────────────────────────────────────
function BrowseDropdown() {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    useEffect(() => {
        function onOut(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
        document.addEventListener('mousedown', onOut);
        return () => document.removeEventListener('mousedown', onOut);
    }, []);

    const isActive = OBJECTS.some(o =>
        location.pathname === o.listPath || location.pathname.startsWith(o.listPath + '/')
    );

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                onClick={() => setOpen(v => !v)}
                style={{
                    ...N.ghostBtn,
                    color: isActive ? '#bb86fc' : '#ccc',
                    backgroundColor: open ? '#2c2c2c' : 'transparent',
                    borderColor: open ? '#555' : '#444',
                }}
            >
                Browse <span style={N.chevron}>{open ? '▴' : '▾'}</span>
            </button>
            {open && (
                <div style={N.menu}>
                    {OBJECTS.map(o => {
                        const active = location.pathname === o.listPath;
                        return (
                            <Link
                                key={o.key}
                                to={o.listPath}
                                style={{
                                    ...N.menuItem,
                                    color: active ? '#bb86fc' : '#e0e0e0',
                                    backgroundColor: active ? '#2a2040' : 'transparent',
                                }}
                                onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = '#2a2a2a'; }}
                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = active ? '#2a2040' : 'transparent'; }}
                            >
                                {o.label}
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── Add Dropdown ──────────────────────────────────────────────────────────────
function AddDropdown() {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    useEffect(() => {
        function onOut(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
        document.addEventListener('mousedown', onOut);
        return () => document.removeEventListener('mousedown', onOut);
    }, []);

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                onClick={() => setOpen(v => !v)}
                style={{
                    ...N.addBtn,
                    backgroundColor: open ? '#a370e3' : '#bb86fc',
                }}
            >
                + Add <span style={N.chevron}>{open ? '▴' : '▾'}</span>
            </button>
            {open && (
                <div style={{ ...N.menu, right: 0, left: 'auto', minWidth: '160px' }}>
                    {OBJECTS.map(o => (
                        <Link
                            key={o.key}
                            to={o.addPath}
                            style={N.addMenuItem}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#a370e3'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#bb86fc'}
                        >
                            {o.addLabel}
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Profile Dropdown ──────────────────────────────────────────────────────────
function ProfileDropdown({ signOut, user }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [displayName, setDisplayName] = useState(null);
    const ref = useRef(null);

    // Load display name from API on mount
    useEffect(() => {
        async function loadUser() {
            try {
                const { tokens } = await fetchAuthSession();
                const idToken = tokens.idToken.toString();
                const res = await axios.get(`${config.API_URL}/user`, {
                    headers: { Authorization: idToken }
                });
                setDisplayName(res.data.display_name || null);
            } catch (e) {
                // silently fail — initials fallback will show
            }
        }
        loadUser();
    }, []);

    // Close on outside click
    useEffect(() => {
        function onOut(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
        document.addEventListener('mousedown', onOut);
        return () => document.removeEventListener('mousedown', onOut);
    }, []);

    // Build initials for avatar
    const email = user?.signInDetails?.loginId || user?.username || '';
    const initials = displayName
        ? displayName.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2)
        : email ? email[0].toUpperCase()
        : '?';

    const handleMyProfile  = () => { setOpen(false); navigate('/profile'); };
    const handleMyShows    = () => { setOpen(false); navigate('/my-shows'); };
    const handleMyImages   = () => { setOpen(false); navigate('/my-images'); };
    const handleAdminPortal = () => { setOpen(false); navigate('/admin'); };
    const handleSignOut    = () => { setOpen(false); signOut(); };

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                onClick={() => setOpen(v => !v)}
                style={{
                    ...N.profileBtn,
                    borderColor: open ? '#bb86fc' : '#555',
                    backgroundColor: open ? '#2a2040' : '#2c2c2c',
                }}
                title={displayName || email}
            >
                <span style={N.profileInitials}>{initials}</span>
                <span style={N.chevron}>{open ? '▴' : '▾'}</span>
            </button>

            {open && (
                <div style={{ ...N.menu, right: 0, left: 'auto', minWidth: '180px' }}>
                    {/* Name / email header */}
                    <div style={N.profileMenuHeader}>
                        <div style={N.profileMenuName}>{displayName || 'My Account'}</div>
                        {displayName && <div style={N.profileMenuEmail}>{email}</div>}
                    </div>
                    <div style={N.profileMenuDivider} />
                    <button style={N.profileMenuItem} onClick={handleMyProfile}>My Profile</button>
                    <button style={N.profileMenuItem} onClick={handleMyShows}>My Shows</button>
                    <button style={N.profileMenuItem} onClick={handleMyImages}>My Images</button>
                    <button style={N.profileMenuItem} onClick={handleAdminPortal}>Admin Portal</button>
                    <div style={N.profileMenuDivider} />
                    <button style={{ ...N.profileMenuItem, color: '#e57373' }} onClick={handleSignOut}>Sign Out</button>
                </div>
            )}
        </div>
    );
}

// ── NavBar ────────────────────────────────────────────────────────────────────
function NavBar({ signOut, user }) {
    const location = useLocation();
    return (
        <nav style={N.nav}>
            {/* Logo — left edge */}
            <Link to="/" style={N.brandLink}>
                <img src={sharcLogo} alt="SHARC" style={N.logo} />
            </Link>

            {/* Search — fixed width, right next to logo */}
            <GlobalSearch />

            {/* Flexible spacer pushes everything else to the right */}
            <div style={{ flex: 1 }} />

            {/* Right-side controls */}
            <div style={N.rightGroup}>
                <Link
                    to="/projects"
                    style={{
                        ...N.ghostBtn,
                        color: location.pathname.startsWith('/projects') ? '#bb86fc' : '#ccc',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                    }}
                >
                    Projects
                </Link>
                <BrowseDropdown />
                <AddDropdown />
                <div style={N.divider} />
                <ProfileDropdown signOut={signOut} user={user} />
            </div>
        </nav>
    );
}

// ── App ───────────────────────────────────────────────────────────────────────
function App() {
    return (
        <Authenticator hideSignUp={true}>
            {({ signOut, user }) => (
                <Router>
                    <div style={S.app}>
                        <NavBar signOut={signOut} user={user} />
                        <main style={S.main}>
                            <Routes>
                                <Route path="/" element={<Home />} />
                                <Route path="/how-it-works" element={<HowItWorks />} />
                                <Route path="/my-shows-tutorial" element={<MyShowsTutorial />} />
                                <Route path="/artists" element={<ArtistList />} />
                                <Route path="/artists/:slug" element={<ArtistDetail />} />
                                <Route path="/venues" element={<VenueList />} />
                                <Route path="/venues/:slug" element={<VenueDetail />} />
                                <Route path="/events" element={<EventList />} />
                                <Route path="/events/:slug" element={<EventDetail />} />
                                <Route path="/artists/:slug/edit" element={<EditArtist />} />
                                <Route path="/venues/:slug/edit" element={<EditVenue />} />
                                <Route path="/events/:slug/edit" element={<EditEvent />} />
                                <Route path="/add-artist" element={<AddArtist />} />
                                <Route path="/add-venue" element={<AddVenue />} />
                                <Route path="/add-event" element={<AddEvent />} />
                                <Route path="/projects" element={<ProjectList />} />
                                <Route path="/projects/:id" element={<ProjectDetail />} />
                                <Route path="/projects/:id/edit" element={<EditProject />} />
                                <Route path="/add-project" element={<AddProject />} />
                                <Route path="/festivals" element={<FestivalList />} />
                                <Route path="/festivals/:slug" element={<FestivalDetail />} />
                                <Route path="/festivals/:slug/edit" element={<EditFestival />} />
                                <Route path="/add-festival" element={<AddFestival />} />
                                <Route path="/promoters" element={<PromoterList />} />
                                <Route path="/promoters/:slug" element={<PromoterDetail />} />
                                <Route path="/promoters/:slug/edit" element={<EditPromoter />} />
                                <Route path="/add-promoter" element={<AddPromoter />} />
                                <Route path="/members" element={<MemberList />} />
                                <Route path="/members/:slug" element={<MemberDetail />} />
                                <Route path="/members/:slug/edit" element={<EditMember />} />
                                <Route path="/add-member" element={<AddMember />} />
                                <Route path="/production-role-mapping/:memberId" element={<ProductionRoleMapping />} />
                                <Route path="/admin" element={<AdminPortal />} />
                                <Route path="/admin/picklists" element={<AdminPicklists />} />
                                <Route path="/admin/bulk-edit-artists" element={<AdminBulkEditArtists />} />
                                <Route path="/admin/change-log" element={<ChangeLog />} />
                                <Route path="/:sharcId" element={<SharcIdPage />} />
                                <Route path="/profile" element={<MyProfile />} />
                                <Route path="/my-shows" element={<MyShows />} />
                                <Route path="/my-images" element={<MyImages />} />
                            </Routes>
                        </main>
                        <Footer />
                    </div>
                </Router>
            )}
        </Authenticator>
    );
}

// ── Nav styles (N) ────────────────────────────────────────────────────────────
const N = {
    nav: {
        backgroundColor: '#1e1e1e',
        borderBottom: '1px solid #333',
        padding: '0 20px',
        display: 'flex',
        alignItems: 'center',
        height: '60px',
        position: 'sticky',
        top: 0,
        zIndex: 200,
        gap: '12px',
    },
    logo: { height: '28px', width: 'auto', display: 'block' },
    brandLink: { display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 },

    // Search
    searchOuter: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        width: '380px',
    },
    objSelectWrap: {
        position: 'relative',
        flexShrink: 0,
    },
    objSelect: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRight: 'none',
        borderRadius: '4px 0 0 4px',
        color: '#e0e0e0',
        fontSize: '13px',
        padding: '8px 28px 8px 10px',
        height: '36px',
        cursor: 'pointer',
        outline: 'none',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
    },
    objChevron: {
        position: 'absolute',
        right: '7px',
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: '11px',
        color: '#999',
        pointerEvents: 'none',
        lineHeight: 1,
    },
    searchInput: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderLeft: 'none',
        borderRadius: '0 4px 4px 0',
        color: '#e0e0e0',
        fontSize: '14px',
        padding: '8px 12px',
        width: '100%',
        height: '36px',
        boxSizing: 'border-box',
        outline: 'none',
    },
    searchDropdown: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderTop: 'none',
        borderRadius: '0 0 6px 6px',
        maxHeight: '340px',
        overflowY: 'auto',
        zIndex: 300,
        boxShadow: '0 8px 24px rgba(0,0,0,0.55)',
    },
    searchMeta: {
        padding: '10px 14px',
        fontSize: '13px',
        color: '#777',
    },
    searchRow: {
        padding: '9px 14px',
        cursor: 'pointer',
        borderBottom: '1px solid #333',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        backgroundColor: 'transparent',
    },
    searchRowLabel: { fontSize: '14px', color: '#e0e0e0' },
    searchRowSub:   { fontSize: '12px', color: '#888' },

    // Browse / Add
    rightGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexShrink: 0,
    },
    ghostBtn: {
        background: 'transparent',
        border: '1px solid #444',
        borderRadius: '4px',
        color: '#ccc',
        fontSize: '14px',
        padding: '7px 13px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        whiteSpace: 'nowrap',
    },
    addBtn: {
        backgroundColor: '#bb86fc',
        border: 'none',
        borderRadius: '4px',
        color: '#121212',
        fontSize: '14px',
        fontWeight: 'bold',
        padding: '7px 14px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        whiteSpace: 'nowrap',
    },
    chevron: { fontSize: '10px', opacity: 0.75 },
    menu: {
        position: 'absolute',
        top: 'calc(100% + 5px)',
        left: 0,
        backgroundColor: '#1e1e1e',
        border: '1px solid #444',
        borderRadius: '6px',
        minWidth: '160px',
        zIndex: 300,
        boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        overflow: 'hidden',
    },
    menuItem: {
        display: 'block',
        padding: '10px 16px',
        fontSize: '14px',
        textDecoration: 'none',
        color: '#e0e0e0',
    },
    addMenuItem: {
        display: 'block',
        padding: '10px 16px',
        fontSize: '14px',
        fontWeight: '600',
        textDecoration: 'none',
        color: '#121212',
        backgroundColor: '#bb86fc',
        borderBottom: '1px solid rgba(0,0,0,0.12)',
        textAlign: 'right',
    },
    divider: {
        width: '1px',
        height: '24px',
        backgroundColor: '#444',
        margin: '0 2px',
    },
    signOutBtn: {
        backgroundColor: 'transparent',
        color: '#aaa',
        border: '1px solid #444',
        borderRadius: '4px',
        padding: '7px 12px',
        fontSize: '13px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    // Profile dropdown
    profileBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        backgroundColor: '#2c2c2c',
        border: '1px solid #555',
        borderRadius: '6px',
        padding: '5px 10px',
        cursor: 'pointer',
        color: '#e0e0e0',
        fontSize: '13px',
        whiteSpace: 'nowrap',
        transition: 'border-color 0.15s, background-color 0.15s',
    },
    profileInitials: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '26px',
        height: '26px',
        borderRadius: '4px',
        backgroundColor: '#bb86fc',
        color: '#121212',
        fontSize: '12px',
        fontWeight: 'bold',
        letterSpacing: '0.5px',
        flexShrink: 0,
    },
    profileMenuHeader: {
        padding: '10px 16px 8px',
    },
    profileMenuName: {
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#e0e0e0',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    profileMenuEmail: {
        fontSize: '11px',
        color: '#777',
        marginTop: '2px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    profileMenuDivider: {
        height: '1px',
        backgroundColor: '#333',
        margin: '2px 0',
    },
    profileMenuItem: {
        display: 'block',
        width: '100%',
        padding: '10px 16px',
        fontSize: '14px',
        color: '#e0e0e0',
        background: 'none',
        border: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
};

// ── Page styles (S) ───────────────────────────────────────────────────────────
const S = {
    app: { minHeight: '100vh', backgroundColor: '#121212', display: 'flex', flexDirection: 'column' },
    main: { width: '100%', maxWidth: '1200px', margin: '0 auto', padding: '32px 24px', flex: 1, minWidth: 0, boxSizing: 'border-box' },
};

export default App;
